import * as React from 'react';
import classNames from 'classnames';

import { Res } from '../../api';
import { Binding, Result, Guid } from 'core/infrastructure';

import * as Utils from './Utils';
import { FormComponent } from './Form';
import ErrorBlock from './ErrorBlock';
import Button from './Button';

interface EditableStringItem {
	key: string;
	value: string;
}

interface Props<T> extends React.HTMLProps<EditableStringList<T>> {
	listHeight?: number;
	linkTo: Binding.PropertyPath<T>;
	boundValue?: string[];
	placeholder?: string;
	allowDuplicate?: boolean;
	onItemAdded?: (newItem: string) => void;
	onItemRemoved?: (removedItem: Nullable<string>) => void;
	allowReorder?: boolean;
}

interface State {
	items: EditableStringItem[];
	selectedItem: Nullable<string>;
	newItem: EditableStringItem;
	errorMsg?: string;
}

export function emptyItem(): EditableStringItem {
	return {
		key: '',
		value: ''
	};
}

export class EditableStringList<T> extends React.Component<Props<T>, State> implements FormComponent {

	private ulItems = React.createRef<HTMLUListElement>();
	private input = React.createRef<HTMLInputElement>();

	constructor(props: Props<T>) {
		super(props);
		const items = props.boundValue
			? this.getItems(props.boundValue)
			: [];
		this.state = { items: [...items], newItem: emptyItem(), selectedItem: null };

		this.onMoveItemUp = this.onMoveItemUp.bind(this);
		this.onMoveItemDown = this.onMoveItemDown.bind(this);
		this.onAddItem = this.onAddItem.bind(this);
		this.onRemoveItem = this.onRemoveItem.bind(this);
		this.onNewItemChange = this.onNewItemChange.bind(this);
	}

	getPath() {
		this.setState({ errorMsg: '' });
		return Binding.propertyPathToString(this.props.linkTo);
	}

	showErrors(errors: string[]) {
		this.setState({ errorMsg: errors.join('') });
	}

	validate() {
		const errors = [];

		if (this.state.newItem.key.trim() === '') {
			errors.push(Res.Msg.MSG_CANNOT_ADD_EMPTY_ELEMENT());
		}

		if (!this.props.allowDuplicate && this.state.items.findIndex(x => x.key === this.state.newItem.key) >= 0) {
			errors.push(Res.Msg.MSG_CANNOT_ADD_DUPLICATE_ELEMENT());
		}

		this.showErrors(errors);
		return errors.length === 0;
	}

	getValue(): Result<string[]> {
		return Result.ok(this.state.items.map(i => i.value));
	}

	private getItems(items: string[]): EditableStringItem[] {
		if (this.props.allowDuplicate) {
			return [...items].map(i => ({
				key: Guid.create(),
				value: i
			}));
		}

		return [...items].map(i => ({
			key: i,
			value: i
		}));
	}

	private ensureVisible(index: number) {

		// Busco el	inicio del index (en css es 1-based)
		const $li = $(`li:nth-child(${index + 1})`, this.ulItems.current!);

		// OJO: Este código es complicado de tocar. Antes de intentar "arreglarlo", asegúrate
		// de que entiendes lo que tiene que pasar y haz pruebas (muchas) para comprobar que
		// no has roto nada

		// itemTop es relativo al "viewPort" de la lista, no a la pantalla ni a la propia lista.
		// No puedo usar position().top porque eso me da la coordenada relativa al ancestro
		// posicionado por css, y el ul no lo está.

		const itemTop = $li.offset()!.top - $li.parent()!.offset()!.top;
		const itemBottom = $li.outerHeight()! + itemTop;

		// Ajusto el scroll lo justo para que se vea el item
		if (itemBottom > this.ulItems.current!.clientHeight) {
			this.ulItems.current!.scrollTop += itemBottom - this.ulItems.current!.clientHeight;
		}

		if (itemTop < 0) {
			// sumo porque itemTop es negativo (al sumar algo negativo, el scroll va hacia arriba)
			this.ulItems.current!.scrollTop += itemTop;
		}
	}

	private onMoveItemUp() {
		if (!this.state.selectedItem)
			return;

		const items = [...this.state.items];
		const fromIndex = items.map(i => i.key).indexOf(this.state.selectedItem);
		if (fromIndex > 0) {
			items.moveItem(fromIndex, fromIndex - 1);
			this.setState({ items }, () => this.ensureVisible(fromIndex - 1));
		}
	}

	private onMoveItemDown() {
		if (!this.state.selectedItem)
			return;

		const items = [...this.state.items];
		const fromIndex = items.map(i => i.key).indexOf(this.state.selectedItem);
		if (fromIndex >= 0 && fromIndex < items.length - 1) {
			items.moveItem(fromIndex, fromIndex + 1);
			this.setState({ items }, () => this.ensureVisible(fromIndex + 1));
		}
	}

	private onAddItem() {
		if (!this.validate())
			return;

		const newItems = [...this.state.items, this.state.newItem];

		this.setState({
			items: newItems,
			newItem: emptyItem(),
			selectedItem: this.state.newItem.key
		}, () => {
			this.props.onItemAdded?.(this.state.newItem.value);
			this.ensureVisible(newItems.length - 1);
			// Devolvemos el foco al input para seguir introduciendo valores
			this.input.current!.focus();
		});
	}

	private onRemoveItem() {
		const removedItem = this.state.items.filter(i => i.key === this.state.selectedItem)[0];

		this.setState({
			items: this.state.items.filter(x => x.key !== this.state.selectedItem),
			selectedItem: null
		}, () => {
			this.props.onItemRemoved?.(removedItem.value);
		});
	}

	private onNewItemChange(event: React.ChangeEvent<HTMLInputElement>) {
		const newItem = this.props.allowDuplicate
			? { key: Guid.create(), value: event.target.value }
			: { key: event.target.value, value: event.target.value };
		this.setState({ newItem });
	}

	render() {

		const listStyle: React.CSSProperties = {
			border: '1px solid #e4e5e7',
			borderRadius: 4,
			height: this.props.listHeight || 300,
			overflow: 'auto'
		};

		const itemStyle = {
			border: 'none',
			borderBottom: '1px solid #e4e5e7',
			cursor: 'pointer'
		};

		const items = this.state.items.map((item, idx) => {
			const className = classNames({
				'list-group-item': true,
				'selected': this.state.selectedItem === item.key
			});

			const onClick = () => this.setState({ selectedItem: item.key });
			return <li key={idx} className={className} onClick={onClick} style={itemStyle}>{item.value}</li>;
		});

		const inputClasses = classNames({
			'm-b-xs': true,
			'input-group': true,
		});

		const addButtonStyle = {
			marginRight: -4,
			width: 39
		};

		const allowReorder = this.props.allowReorder === undefined || this.props.allowReorder;

		return (
			<div>
				<div className={classNames({ 'has-error': !!this.state.errorMsg })} >
					<div className={inputClasses}>
						<input ref={this.input} value={this.state.newItem.value} placeholder={this.props.placeholder || ''} className='form-control' onChange={this.onNewItemChange} onKeyDown={Utils.whenEnterKey(this.onAddItem)} />
						<span className='input-group-btn'>
							<Button onClick={this.onAddItem} style={addButtonStyle}><i className='fa fa-plus text-info'></i></Button>
						</span>
					</div>
					<ErrorBlock msg={this.state.errorMsg} />
				</div>

				<div className='multi-select-list'>
					<ul className='list-group' style={listStyle} ref={this.ulItems}>
						{items}
					</ul>
				</div>
				<div className='multi-select-list-buttons'>
					<div className='m-t-md'>
						<Button onClick={this.onRemoveItem}><i className='text-info fa fa-trash-o'></i></Button>
					</div>
					{
						allowReorder
							? <div className='m-t btn-group-vertical'>
								<Button onClick={this.onMoveItemUp}><i className='text-info fa fa-arrow-up'></i></Button>
								<Button onClick={this.onMoveItemDown} className='m-t-xs'><i className='text-info fa fa-arrow-down'></i></Button>
							</div>
							: null
					}

				</div>
			</div>
		);
	}
}

export type EditableStringListCtor<T> = new (props: Props<T>) => React.Component<Props<T>, State>;
